import { domToCanvas } from "modern-screenshot";
import { applyImprint, type ImprintContext } from "./imprint.js";

/**
 * Generic capture: rasterise `target` (a CSS selector) to a PNG Blob with the
 * brand imprint drawn on. `domToCanvas` captures the element's full height
 * regardless of page scroll, so a pane taller than the viewport comes out
 * complete. Nodes tagged `data-screenshot-exclude="true"` (e.g. the Share
 * button itself) are filtered out.
 *
 * Apps needing special handling (e.g. GammaLens's inner-scroll SVG) pass their
 * own `capture` to <ShareButton/> instead of using this default.
 *
 * Throws if the element is missing or the canvas encode fails.
 */
export async function captureElement(target: string, ctx: ImprintContext): Promise<Blob> {
  const el = document.querySelector<HTMLElement>(target);
  if (!el) throw new Error(`captureElement: "${target}" not found in DOM`);

  const canvas = await domToCanvas(el, {
    scale: Math.min(window.devicePixelRatio || 1, 2),
    backgroundColor: "#0d1220",
    filter: (node) => {
      if (!(node instanceof HTMLElement)) return true;
      return node.dataset["screenshotExclude"] !== "true";
    },
  });

  applyImprint(canvas, ctx);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) reject(new Error("captureElement: canvas.toBlob returned null"));
      else resolve(blob);
    }, "image/png");
  });
}
